import _ from 'lodash'
import { DateTime } from 'luxon'
import { Country } from 'country-state-city'

import { authApi, resourceApi } from './api'

DateTime.prototype.toOrdinalDate = function () {
  const getDayWithOrdinal = (day) => {
    const j = day % 10,
      k = day % 100
    if (j === 1 && k !== 11) {
      return day + 'st'
    }
    if (j === 2 && k !== 12) {
      return day + 'nd'
    }
    if (j === 3 && k !== 13) {
      return day + 'rd'
    }
    return day + 'th'
  }
  const dayWithOrdinal = getDayWithOrdinal(this.day)
  return `${this.monthShort} ${dayWithOrdinal}, ${this.toFormat('yyyy')}`
}

DateTime.prototype.toOrdinalDateAndTime = function () {
  const getDayWithOrdinal = (day) => {
    const j = day % 10,
      k = day % 100
    if (j === 1 && k !== 11) {
      return day + 'st'
    }
    if (j === 2 && k !== 12) {
      return day + 'nd'
    }
    if (j === 3 && k !== 13) {
      return day + 'rd'
    }
    return day + 'th'
  }
  const dayWithOrdinal = getDayWithOrdinal(this.day)
  const formattedTime = this.toFormat('hh:mm:ss a')
  return `${this.monthShort} ${dayWithOrdinal} ${this.toFormat('yyyy')}, ${formattedTime}`
}

export { DateTime }

export const isDirectAccessEnabled = (value) =>
  value === true || value === 1 || value === '1' || value === 'true'

export const getAvatar = (user) => {
  if (user == null) return null
  const colors = [
      'red',
      'pink',
      'purple',
      'deep-purple',
      'indigo',
      'blue',
      'light-blue',
      'cyan',
      'teal',
      'green',
      'light-green',
      'lime',
      'yellow',
      'amber',
      'orange',
      'deep-orange',
      'brown',
      'blue-grey',
      'grey'
    ]
    const shades = _.range(1, 5)
    const initials = user?.name
      ? user.name
          .trim()
          .split(/\s+/)
          .map((word) => word.trim()[0].toUpperCase())
          .join('')
      : 'AC'
    const hashedString = `${user.email || ''}-${initials}`
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0)
    return {
      initials,
      color: colors[hashedString % colors.length],
      shade: shades[hashedString % shades.length],
      picture: user.picture || null
    }
}

export const getAllCountries = Country.getAllCountries().reduce(
  (a, { isoCode, ...rest }) => {
    a[isoCode] = rest
    return a
  },
  {}
)

export const getRecords = async (
  resource,
  params,
  errorMessage,
  isActive = true,
  isAuthApi = false
) => {
  try {
    const api = isAuthApi
      ? authApi.extend(resource)
      : resourceApi.extend(resource)
    params.active = isActive
    const result = await api.get('', { params })
    let error = null
    ;({ error, [resource]: resource } = result.data.response)
    if (error) return true
    if (_.isObjectLike(resource) && !_.isEmpty(resource)) return errorMessage
    return true
    // eslint-disable-next-line no-unused-vars
  } catch (e) {
    return true
  }
}

export const defaultItemsPerPage = 50

export const roleColors = {
  // Deep Purple - Authority/Admin (Kept as primary brand)
  ADM: {
    dark: [179, 157, 219], // Soft Lavender
    light: [103, 58, 183] // Rich Purple
  },
  // Emerald/Sage - Instructional/Growth (Differentiates from ADM)
  INS: {
    dark: [165, 214, 167], // Pale Sage Green
    light: [46, 125, 50] // Forest Green
  },
  // Amber/Orange - Student/Active (High contrast to INS/ADM)
  LRN: {
    dark: [255, 202, 121], // Soft Peach/Amber
    light: [255, 143, 0] // Deep Vibrant Orange
  },
  // Rose/Pink - Developer/System (Distinct from the user roles)
  DEV: {
    dark: [244, 143, 177], // Pastel Rose
    light: [173, 20, 87] // Bold Magenta
  }
}

export const formatDate = (dateString, includeTime=false) => {
  if (!dateString) return ''
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
    timeStyle: includeTime?'short':undefined
  }).format(new Date(dateString))
}

export const rules = {
  auth: {
    email: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'Email is required.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value))
          return true
        return 'Email must be valid.'
      },
      async (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        return await getRecords(
          'user',
          { where: { email: value } },
          'Email address already in use.',
          true,
          true
        )
      }
    ],
    password: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'Password is required.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (
          /^(?=.*[A-Za-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[^\s]+$/.test(value)
        ) {
          return true
        }
        return 'Password must contain at least one uppercase letter, one digit, one special character, and no spaces.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 12) return true
        return 'Password must be at least 12 characters long.'
      }
    ],
    name: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'Full name is required.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 3) return true
        return 'Full name must be at least 3 characters long.'
      }
    ],
    username: [
      (value) => {
        if (!value) return true
        value = value.replace(/^\s+|\s+$/g, '')
        if (/^[a-zA-Z0-9._-]{3,30}$/.test(value)) return true
        return 'Username must be 3–30 characters and contain only letters, numbers, dots, hyphens or underscores.'
      }
    ],
    mobile: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The mobile number is mandatory.'
      }
    ],
    otp: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (/^\d+$/.test(value)) return true
        return 'The OTP code can only contain digits.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length == 6) return true
        return 'The OTP code can must contain 6 digits.'
      }
    ]
  },
  user: {
    name: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'Full name is required.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 3) return true
        return 'Full name must be at least 3 characters long.'
      }
    ],
    roles: [
      (value) => {
        if (!_.isEmpty(value) && _.isArrayLikeObject(value)) return true
        return 'Assigning at least one user role is mandatory.'
      }
    ]
  },
  institution: {
    name: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'Institution name is required.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 3) return true
        return 'Institution name must be at least 3 characters long.'
      },
      async (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        return await getRecords(
          'institution',
          { where: { name: value } },
          'Institution name already in use.',
          true
        )
      }
    ],
    country: [
      (value) => {
        if (!_.isEmpty(value) && _.isObjectLike(value)) return true
        return 'The country cannot be missing.'
      }
    ]
  },
  institutionLicense: {
    institution: [
      (value) => {
        if (value != null && !_.isEmpty(value) && _.isObjectLike(value))
          return true
        return 'The institution cannot be missing.'
      }
    ],
    validFrom: [
      (value) => {
        if (value) return true
        return 'Valid from date is required.'
      },
      (value) => {
        const date = new Date(value)
        if (date.toString() !== 'Invalid Date') return true
        return 'Valid from must be a valid date.'
      }
    ],
    validTo: [
      (value) => {
        if (value) return true
        return 'Valid to date is required.'
      },
      (value) => {
        const date = new Date(value)
        if (date.toString() !== 'Invalid Date') return true
        return 'Valid to must be a valid date.'
      }
    ],
    maxDevices: [
      (value) => {
        if (value != null && value !== '') return true
        return 'Maximum device licenses is required.'
      },
      (value) => {
        const num = Number(value)
        if (Number.isInteger(num) && num > 0) return true
        return 'Maximum device licenses must be a positive integer.'
      }
    ],
    department: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value == null || value.length <= 200) return true
        return 'Department cannot exceed 200 characters.'
      }
    ],
    appMode: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value == null || value.length <= 20) return true
        return 'App mode cannot exceed 20 characters.'
      }
    ]
  },
  simulation: {
    title: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The Title is mandatory.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 3) return true
        return 'The Title must be at least 3 characters long.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length <= 100) return true
        return 'The Title cannot exceed 100 characters.'
      }
    ],
    description: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The Description is mandatory.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 5) return true
        return 'The Description must be at least 5 characters long.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length <= 1000) return true
        return 'The Description cannot exceed 1000 characters.'
      }
    ],
    hero: [
      (value) => {
        if (_.isArray(value) && value.lenth > 1)
          return 'Only one simulation Hero Image is allowed.'
        return true
      },
      (value) => {
        const allowedFileTypes = [
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/webp'
        ]
        if (value != null) {
          value = _.isArray(value) ? value[0] : value
          if (allowedFileTypes.includes(value.type)) return true
          return 'The Hero Image must be an image file type.'
        }
        return true
      }
    ],
    estimatedDuration: [
      (value) => {
        if (value != null) value = String(value).replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The Estimated Duration is mandatory.'
      },
      (value) => {
        if (value > 0) return true
        return 'The Estimated Duration cannot be less than a minute.'
      },
      (value) => {
        if (value <= 120) return true
        return 'The Estimated Duration cannot exceed 120 minutes.'
      }
    ],
    author: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if ([null, ''].includes(value) || value.length >= 3) return true
        return 'The Author must be at least 3 characters long.'
      }
    ],
    profile: {
      name: [
        (value) => {
          if (value == null || value == '') return true
          else {
            value = value.replace(/^\s+|\s+$/g, '')
            if (value.length >= 3) return true
            return 'The Name must be at least 3 characters long.'
          }
        }
      ],
      dob: [
        (value) => {
          if (value == null || value == '') return true
          else {
            value = value.replace(/^\s+|\s+$/g, '')
            if (value.length >= 4) return true
            return 'Unsupported format for the Date of Birth. Expected: MM-DD.'
          }
        }
      ],
      age: {
        years: [
          (value) => {
            if (value == null || value == '') return true
            else {
              if (value <= 120) return true
              return 'The Age cannot exceed 120 Years.'
            }
          }
        ],
        months: [
          (value) => {
            if (value == null || value == '') return true
            else {
              if (value <= 36) return true
              return 'The Age in Months cannot exceed 3 Years (36 Months).'
            }
          }
        ],
        weeks: [
          (value) => {
            if (value == null || value == '') return true
            else {
              if (value <= 100) return true
              return 'The Age in Weeks cannot exceed 100.'
            }
          }
        ],
        days: [
          (value) => {
            if (value == null || value == '') return true
            else {
              if (value <= 100) return true
              return 'The Age in Days cannot exceed 100.'
            }
          }
        ]
      },
      religion: [
        (value) => {
          if (value == null || value == '') return true
          else {
            value = value.replace(/^\s+|\s+$/g, '')
            if (value.length >= 2) return true
            return 'The Religion must be at least 2 characters long.'
          }
        }
      ],
      language: [
        (value) => {
          if (value == null || value == '') return true
          else {
            value = value.replace(/^\s+|\s+$/g, '')
            if (value.length >= 3) return true
            return 'The Primary Language must be at least 3 characters long.'
          }
        }
      ],
      history: [
        (value) => {
          if (value == null || value == '') return true
          else {
            value = value.replace(/^\s+|\s+$/g, '')
            if (value.length >= 3) return true
            return 'The Social History must be at least 5 characters long.'
          }
        },
        (value) => {
          if (value.length <= 500) return true
          return 'The Social History cannot exceed 500 characters.'
        }
      ],
      person: [
        (value) => {
          if (value == null || value == '') return true
          else {
            value = value.replace(/^\s+|\s+$/g, '')
            if (value.length >= 3) return true
            return 'The Support Person must be at least 3 characters long.'
          }
        }
      ]
    }
  },
  resource: {
    title: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The Title is mandatory.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 3) return true
        return 'The Title must be at least 3 characters long.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length <= 100) return true
        return 'The Title cannot exceed 100 characters.'
      }
    ],
    description: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The Description is mandatory.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 5) return true
        return 'The Description must be at least 5 characters long.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length <= 1000) return true
        return 'The Description cannot exceed 1000 characters.'
      }
    ]
  },
  resourceCategory: {
    name: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The category name cannot be missing.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 3) return true
        return 'The category name must be at least 3 characters long.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length <= 100) return true
        return 'The category name cannot exceed 100 characters.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        const reserved = [
          'uncategorized',
          'all',
          'root',
          'default',
          'none',
          'null',
          'undefined',
          'category',
          'categories',
          'public',
          'private'
        ]
        if (!reserved.includes(value.replace(/^_+|_+$/g, '').toLowerCase()))
          return true
        return `"${value}" is a reserved category name.`
      }
    ]
  },
  feedback: {
    subject: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The Subject is mandatory.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 3) return true
        return 'The Subject must be at least 3 characters long.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length <= 300) return true
        return 'The Subject cannot exceed 300 characters.'
      }
    ],
    message: [
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value) return true
        return 'The Message is mandatory.'
      },
      (value) => {
        if (value != null) value = value.replace(/^\s+|\s+$/g, '')
        if (value.length >= 5) return true
        return 'The Message must be at least 5 characters long.'
      }
    ]
  }
}